import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ApiService } from '../../core/services/api';
import { FormsModule } from '@angular/forms';
import { NotificationService } from '../../core/services/notification';

@Component({
  selector: 'app-courses',
  standalone: true,
  imports: [CommonModule, TranslateModule, FormsModule],
  template: `
    <div class="bg-white py-12">
      <div class="container mx-auto px-4">
        <!-- Header -->
        <div class="text-center mb-16">
          <h1 class="text-4xl font-bold mb-4 uppercase text-enduro-black">{{ 'NAV.COURSES' | translate }}</h1>
          <div class="w-24 h-1 bg-enduro-orange mx-auto mb-8"></div>
          <p class="text-gray-600 max-w-2xl mx-auto">
            Migliora la tua tecnica con i nostri corsi dedicati a tutti i livelli.
            Dall'amatore al pro, i nostri istruttori certificati ti guideranno.
          </p>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <!-- Courses List -->
          <div>
            <h2 class="text-2xl font-bold mb-6 text-enduro-black border-l-4 border-enduro-orange pl-4">Corsi Disponibili</h2>
            @if (loading()) {
               <div class="p-4 bg-gray-50 rounded">Caricamento corsi...</div>
            } @else {
              <div class="space-y-6">
                @for (course of courses(); track course.id) {
                    <div class="bg-gray-50 rounded-lg p-6 hover:shadow-md transition-shadow border-l-4 border-transparent hover:border-enduro-green">
                        <div class="flex justify-between items-start mb-2">
                            <h3 class="text-xl font-bold text-gray-900">{{ course.title_it }}</h3>
                            <span class="bg-enduro-green text-white text-xs px-2 py-1 rounded font-bold">{{ course.duration_days }} Giorni</span>
                        </div>
                        <p class="text-gray-600 mb-4">{{ course.description_it }}</p>
                        <div class="flex flex-col sm:flex-row justify-between items-center text-sm gap-2">
                           <span class="font-bold text-enduro-orange text-lg">{{ course.price | currency:'EUR' }}</span>
                           <span class="text-gray-500">{{ course.level }} Level</span>
                        </div>
                    </div>
                }
              </div>
            }
          </div>

          <!-- Request Form -->
          <div class="bg-gray-50 p-8 rounded-xl shadow-lg border-t-4 border-enduro-orange h-fit sticky top-24">
            <h2 class="text-2xl font-bold mb-6 text-enduro-black">Richiedi Informazioni o Iscrizione</h2>
            <form (ngSubmit)="onSubmit()" #courseForm="ngForm" class="space-y-4">
              <div>
                <label class="block text-sm font-bold text-gray-700 mb-1">Nome e Cognome</label>
                <input type="text" [(ngModel)]="formData.full_name" name="full_name" required class="w-full rounded-md border-gray-300 shadow-sm focus:border-enduro-orange focus:ring focus:ring-enduro-orange focus:ring-opacity-50 p-2">
              </div>
              <div>
                <label class="block text-sm font-bold text-gray-700 mb-1">Email</label>
                <input type="email" [(ngModel)]="formData.email" name="email" required email class="w-full rounded-md border-gray-300 shadow-sm focus:border-enduro-orange focus:ring focus:ring-enduro-orange focus:ring-opacity-50 p-2">
              </div>
              <div>
                <label class="block text-sm font-bold text-gray-700 mb-1">Telefono</label>
                <input type="tel" [(ngModel)]="formData.phone" name="phone" class="w-full rounded-md border-gray-300 shadow-sm focus:border-enduro-orange focus:ring focus:ring-enduro-orange focus:ring-opacity-50 p-2">
              </div>
              <div>
                <label class="block text-sm font-bold text-gray-700 mb-1">Corso di Interesse (Opzionale)</label>
                <select [(ngModel)]="formData.course_id" name="course_id" class="w-full rounded-md border-gray-300 shadow-sm focus:border-enduro-orange focus:ring focus:ring-enduro-orange focus:ring-opacity-50 p-2">
                    <option [ngValue]="null">Seleziona un corso...</option>
                    @for (course of courses(); track course.id) {
                        <option [value]="course.id">{{ course.title_it }}</option>
                    }
                </select>
              </div>
              <div>
                <label class="block text-sm font-bold text-gray-700 mb-1">Note</label>
                <textarea [(ngModel)]="formData.notes" name="notes" rows="3" class="w-full rounded-md border-gray-300 shadow-sm focus:border-enduro-orange focus:ring focus:ring-enduro-orange focus:ring-opacity-50 p-2"></textarea>
              </div>
              
              <button type="submit" [disabled]="courseForm.invalid || submitting()" class="w-full bg-enduro-orange text-white font-bold py-3 rounded-md hover:bg-opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed uppercase tracking-wide">
                {{ submitting() ? 'Inviando...' : 'Invia Richiesta' }}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  `
})
export class CoursesComponent {
  api = inject(ApiService);
  notification = inject(NotificationService);

  courses = signal<any[]>([]);
  loading = signal<boolean>(true);
  submitting = signal<boolean>(false);

  formData = {
    full_name: '',
    email: '',
    phone: '',
    course_id: null,
    notes: ''
  };

  constructor() {
    this.loadCourses();
  }

  loadCourses() {
    this.loading.set(true);
    // Ideally create an endpoint for courses, assuming /api/courses exists or using filtered events?
    // User requirement said "Pagina Corsi (con form di richiesta iscrizione)".
    // Assuming we have a courses table (yes we do, verified in seed.sql).
    // But endpoint /api/courses needs to be implemented in backend if not yet (check implementation plan).
    // Plan says "Implement Public Endpoints - /api/courses".
    // I haven't implemented backend endpoint for courses yet.
    // I can mock it or just try to call it and it will fail until I implement backend.

    // I'll call it, and implement backend later.
    this.api.get<any[]>('courses').subscribe({
      next: (data) => {
        this.courses.set(data);
        this.loading.set(false);
      },
      error: (err) => {
        console.error('API Error', err);
        this.loading.set(false);
        // Mock data for UI dev if API fails
        this.courses.set([
          { id: 1, title_it: 'Corso Base Enduro', description_it: 'Per chi inizia.', duration_days: 1, price: 150, level: 'Beginner' },
          { id: 2, title_it: 'Corso Avanzato', description_it: 'Per esperti.', duration_days: 2, price: 300, level: 'Advanced' }
        ]);
      }
    });
  }

  onSubmit() {
    if (this.submitting()) return;
    this.submitting.set(true);

    // Call API to submit request
    this.api.post('course-requests', this.formData).subscribe({
      next: () => {
        this.notification.showSuccess('Richiesta inviata con successo!');
        this.submitting.set(false);
        this.formData = { full_name: '', email: '', phone: '', course_id: null, notes: '' };
      },
      error: (err) => {
        console.error(err);
        // Extract backend error message
        const backendError = err.error?.error || '';

        if (backendError.includes('Invalid email domain')) {
          this.notification.showError('Dominio email non valido');
        } else if (backendError) {
          this.notification.showError(backendError);
        } else {
          this.notification.showError('Errore durante l\'invio. Riprova più tardi.');
        }
        this.submitting.set(false);
      }
    });
  }
}
